import { CentricTypography } from '@core/component/CentricTypography'
import useOrderContext from '@modules/order/hooks/use-order-context'
import formatNumberByLocale from '@utils/format-number-by-locale'
import { useObserver } from 'mobx-react-lite'
import React, { FC, memo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox, Tooltip, makeStyles } from '@material-ui/core'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import QtyNumberInput from '@modules/order/components/CellTemplates/components/QtyNumberInput/QtyNumberInput'
import { SplitIcon } from '@components/UI-Components/svg'
import { stores, strings } from '@stores'
import IAReadOnlyField from './IAReadOnlyField'
import Icon from '@components/std-components/Icons'
import useViewContext from '@modules/order/hooks/use-view-context'
import { InitialAllocationDataStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/InitialAllocationDataStore'
import { InitialAllocationUIStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/InitialAllocationUIStore'
import { getNumberFromLocale } from '@utils/get-number-form-locale'
import Button from '@components/std-components/Button'
import classNames from 'classnames'

interface IADoorQuantityTopFieldsProps {
  rowData: any
  areAllDoorsBooked: boolean
  totalDoorQuantity: number | string
  actualIADoorQuantity: number | string
  IATotalDoorQuantity: number | string
  IAWarehouseQuantity: number | string
  IAProductQuantity: number | string
  disabled: boolean
  splitToDoorHandler: Function
  disableIASplitDoorQty?: boolean
}

const useStyles = makeStyles(theme => ({
  bookUnBookCheckbox: {
    display: 'flex',
    alignItems: 'center',
    width: '150px',
    justifyContent: 'center'
  },
  actualQtyContainer: {
    width: 148,
    height: 105,
    background: theme.palette.background.default,
    borderBottom: '1px solid #eeeeee'
  },
  totalActualQty: {
    padding: 10,
    textAlign: 'center'
  },
  totalLabel: {
    marginBottom: 4
  },
  bookUnbookAllWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 65,
    width: 150,
    borderBottom: '1px solid #eeeeee'
  },
  bookQtyCheckbox: {
    color: theme.palette.text.primary
  },
  bookQtyUnCheckbox: {
    color: theme.palette.text.disabled
  },
  totalIADoorInputBoxWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative'
  },
  IADoorQtyInput: {
    width: 80,
    height: 10,
    marginTop: 0
  },
  splitIcon: {
    display: 'flex',
    padding: 0,
    '& .icon': {
      cursor: 'inherit'
    }
  },
  disabledBtn: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  splitIconContainer: {
    position: 'absolute',
    right: -4
  }
}))

const IADoorQuantityTopFields: FC<IADoorQuantityTopFieldsProps> = ({
  rowData,
  areAllDoorsBooked,
  totalDoorQuantity,
  actualIADoorQuantity,
  IATotalDoorQuantity,
  IAWarehouseQuantity,
  IAProductQuantity,
  disabled,
  splitToDoorHandler,
  disableIASplitDoorQty
}) =>
  useObserver(function useHook() {
    const { t } = useTranslation()
    const {
      uiStore: {
        dataStore: { summary }
      }
    } = useOrderContext()
    const { uiStore } = useViewContext()
    const { updateDeliveryAllocatedProductQuantity } =
      uiStore as InitialAllocationUIStore<InitialAllocationDataStore>

    const {
      productConfiguration: { featureFlags }
    } = stores

    const classes = useStyles()
    const inputRef = useRef<HTMLInputElement>(null)
    if (!rowData) return <></>

    const totalDoorQtyLabel = `${t(
      'order.quantitiesAndBook.cnfTotalQtyLabel',
      'Total Door Quantity'
    )} / ${t(
      'order.initialAllocation.cnfIAActualDoorQtyLabel',
      'Actual IA Door Quantity'
    )}`
    const totalDoorQtyValue = `${formatNumberByLocale(
      totalDoorQuantity
    )} / ${formatNumberByLocale(actualIADoorQuantity)}`
    const highlightIAQuantity =
      featureFlags?.highlightQuantityDiscrepancies &&
      IATotalDoorQuantity > IAProductQuantity

    return (
      <>
        <div className={classes.bookUnbookAllWrapper}>
          {!summary?.doorOrder && (
            <div className={classes.bookUnBookCheckbox}>
              <CentricTypography variant={'body2'}>
                {t('orderTable.cnfBookOrUnBookAll', 'Book / Un-Book All')}
              </CentricTypography>
              <Checkbox
                onChange={e => {}}
                disabled={true}
                checked={areAllDoorsBooked}
                className={
                  areAllDoorsBooked ? classes.bookQtyCheckbox : classes.bookQtyUnCheckbox
                }
                size='small'
                color='default'
                icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
                checkedIcon={<CheckBoxIcon fontSize='small' />}
              />
            </div>
          )}
        </div>
        <IAReadOnlyField label={totalDoorQtyLabel} value={totalDoorQtyValue} />
        <div className={classes.actualQtyContainer}>
          <div className={classes.totalActualQty}>
            <div className={classes.totalLabel}>
              <CentricTypography variant={'body2'}>
                {t(
                  'order.initialAllocation.cnfIASplitTotalQtyLabel',
                  'IA Split Door QTY'
                )}
              </CentricTypography>
            </div>
            <div className={classes.totalIADoorInputBoxWrapper}>
              <QtyNumberInput
                onChange={e => {
                  updateDeliveryAllocatedProductQuantity(
                    rowData?.id,
                    getNumberFromLocale(e.target.value)
                  )
                }}
                disabled={disabled}
                className={classes.IADoorQtyInput}
                value={IATotalDoorQuantity}
                ref={inputRef}
                highlightInput={highlightIAQuantity}
              />

              <Tooltip
                title={t('order.initialAllocation.cnfSplitIAQty', 'Split IA Quantity')}
                className={classes.splitIconContainer}
              >
                <div>
                  <Button
                    onClick={() => {
                      if (disableIASplitDoorQty) {
                        return
                      }
                      splitToDoorHandler(inputRef?.current?.value)
                    }}
                    disabled={disableIASplitDoorQty}
                    variant='link'
                    size='none'
                    className={classNames(classes.splitIcon, {
                      [classes.disabledBtn]: disableIASplitDoorQty
                    })}
                  >
                    <Icon imageSrc={SplitIcon} alt={strings.splitQuantity} />
                  </Button>
                </div>
              </Tooltip>
            </div>
          </div>
          <IAReadOnlyField
            label={t('order.initialAllocation.warehouseQty', 'IA Warehouse QTY')}
            value={formatNumberByLocale(IAWarehouseQuantity)}
            noBottomBorder={true}
          />
        </div>
      </>
    )
  })

export default memo(IADoorQuantityTopFields)
